import { createSelector, createSlice } from '@reduxjs/toolkit';

// Temp fix for circular dependency
import { EmailConfirmBannerShowSecondTimeDelayMs } from '~features/emailConfirm/constants';
import { loadFromStorage, lsKeys, saveToStorage } from '~features/storage';

import type { RootState } from '../index';

type EmailConfirmState = {
	bannerHide: boolean;
	// -1 - never postponed
	// 0 - postponed one time
	// unix time in ms - postponed at
	bannerPostponedAt: number;
	modalOpened: boolean;
	lastSent: number;
};

const initialState = (): EmailConfirmState => {
	const bannerPostponedAt = global.localStorage
		? loadFromStorage(lsKeys.EMAIL_CONFIRM_BANNER_POSTPONED_AT, -1)
		: -1;
	const lastSent = global.localStorage
		? loadFromStorage(lsKeys.EMAIL_CONFIRM_LAST_SENT, 0)
		: 0;

	return {
		bannerHide: false,
		bannerPostponedAt,
		// show only first time
		modalOpened: false,
		lastSent,
	};
};

const emailConfirmSlice = createSlice({
	name: 'emailConfirm',
	initialState,
	reducers: {
		remind: (state) => {
			state.bannerHide = true;
			state.bannerPostponedAt = state.bannerPostponedAt < 0 ? 0 : Date.now();

			if (global.localStorage) {
				saveToStorage(
					lsKeys.EMAIL_CONFIRM_BANNER_POSTPONED_AT,
					state.bannerPostponedAt
				);
			}
		},
		showModal: (state) => {
			state.modalOpened = true;
		},
		hideModal: (state) => {
			state.modalOpened = false;
		},
		storeModalShown: () => {
			if (global.localStorage) {
				saveToStorage(lsKeys.EMAIL_CONFIRM_MODAL_SHOWN, true);
			}
		},
		setLastSent: (state) => {
			state.lastSent = Date.now();
		},
	},
});

const emailConfirm = (state: RootState) => state.emailConfirm;

const getCanShowBanner = createSelector(
	emailConfirm,
	(state) =>
		!state.bannerHide &&
		Date.now() >=
			state.bannerPostponedAt + EmailConfirmBannerShowSecondTimeDelayMs
);

const getIsModalOpened = createSelector(
	emailConfirm,
	(state) => state.modalOpened
);

const getLastSent = createSelector(emailConfirm, (state) => state.lastSent);

export const emailConfirmSelectors = {
	getCanShowBanner,
	getIsModalOpened,
	getLastSent,
};

export const emailConfirmActions = emailConfirmSlice.actions;

export default emailConfirmSlice.reducer;
